// Quiz Matri (<quiz-flow>), rendered by sections/quiz.liquid.
//
// Every screen is already in the HTML: this element picks the one showing
// and hides options no product in play carries. Loaded on first opening:
// quiz-catalog.js (data, ranking) and quiz-result.js (log, result).
// sessionStorage keeps the answers, so the quiz reopens where it stopped.

const STATE = 'matri-quiz';
const CLOSE_FALLBACK = 400;
const PICK = 300; // the Figma holds the selected card ~300ms before moving on
const KEYS = 'ABCDEFGH';

const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Plural copy: two Liquid templates with a [count] slot.
const plural = (el, count) => (count === 1 ? el.dataset.one : el.dataset.other).replace('[count]', count);

// Exit played while still open, as in the cart drawer (Safari top layer).
function closeDialog(dialog) {
  if (!dialog?.open || dialog.hasAttribute('closing')) return;
  dialog.setAttribute('closing', '');
  const finish = () => {
    clearTimeout(timer);
    dialog.removeEventListener('transitionend', finish);
    dialog.removeAttribute('closing');
    dialog.close();
  };
  const timer = setTimeout(finish, CLOSE_FALLBACK);
  dialog.addEventListener('transitionend', finish);
}

class QuizFlow extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.frame = this.querySelector('[data-frame]');
    // Questions are theme blocks: drop empty ones, number the rest.
    this.steps = [...this.querySelectorAll('[data-step]')]
      .filter((el) => el.querySelector('[data-option]') || el.remove())
      .map((el, index) => {
        el.dataset.index = index;
        el.toggleAttribute('data-first', !index);
        el.querySelector('[data-number]').textContent = `0${index + 1}`.slice(-2);
        return { el, ...el.dataset };
      });
    const total = this.steps.length;
    for (const el of this.querySelectorAll('[data-count-steps]')) {
      el.textContent = el.dataset.countSteps.replace('[nn]', `0${total}`.slice(-2)).replace('[n]', total);
    }

    let saved = {};
    try {
      saved = JSON.parse(sessionStorage.getItem(STATE)) || {};
    } catch {
      // Storage blocked: the quiz starts over.
    }
    this.state = { screen: 'intro', step: 0, answers: {}, top: [], ...saved };
    if (!this.steps[this.state.step]) this.state.step = 0;
    this.launch = this.querySelector('[data-launch]');
    this.launch.hidden = Boolean(this.state.hidden);

    this.controller = new AbortController();
    const { signal } = this.controller;

    this.addEventListener('click', this.onClick, { signal });
    this.addEventListener('pointerover', this.onHover, { signal });
    this.addEventListener('focusin', this.onHover, { signal });
    this.dialog.addEventListener('keydown', this.onKey, { signal });

    // Escape fires cancel: hold the instant close, play the exit instead.
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    }, { signal });

    // Any link to #quiz opens it, such as the "Fazer o quiz" home banner.
    document.addEventListener('click', (event) => {
      if (!event.target.closest?.('a[href$="#quiz"]')) return;
      event.preventDefault();
      this.open();
    }, { signal });

    // Theme editor only: selecting the section or a block opens the quiz.
    if (window.Shopify?.designMode) import(this.dataset.editor).then((m) => m.default(this, signal));

    if (window.location.hash === '#quiz') this.open();
  }

  disconnectedCallback() {
    this.controller?.abort();
    clearTimeout(this.timer);
  }

  save() {
    try {
      sessionStorage.setItem(STATE, JSON.stringify(this.state));
    } catch {
      // Not saved: the quiz will not resume.
    }
  }

  close() {
    closeDialog(this.dialog);
  }

  open() {
    if (!this.dialog.open) this.dialog.showModal();
    this.render();
    this.load().then(() => {
      if (this.state.screen === 'step') this.renderStep();
    });
  }

  load() {
    this.loading ??= Promise.all([import(this.dataset.module), import(this.dataset.view)])
      .then(async ([lib, view]) => {
        this.lib = lib;
        this.view = view;
        const keys = this.steps.filter((step) => step.mode !== 'offer').map((step) => step.key);
        this.items = await lib.loadCatalog(this.dataset.catalog, keys);
      })
      .catch(() => {
        // No catalog: every option stays visible and the result says so.
        this.items = null;
      });
    return this.loading;
  }

  onClick = (event) => {
    const option = event.target.closest('[data-option]');
    if (option) return this.choose(option);

    const action = event.target.closest('[data-action]')?.dataset.action;
    if (action === 'open') this.open();
    if (action === 'close') this.close();
    if (action === 'back') this.back();
    // The launcher's close: gone for the rest of the visit.
    if (action === 'dismiss') {
      this.launch.hidden = this.state.hidden = true;
      this.save();
    }
    if (action === 'start' || action === 'redo') {
      this.state.answers = {};
      this.state.top = [];
      this.go('step', 0);
    }
  };

  go(screen, step = this.state.step, direction = 1) {
    clearTimeout(this.timer);
    this.busy = false;
    this.direction = direction;
    Object.assign(this.state, { screen, step });
    this.save();
    this.render();
  }

  current() {
    return this.frame.querySelector('[data-screen]:not([hidden])');
  }

  render() {
    const { screen, step } = this.state;
    this.frame.dataset.screen = screen;
    for (const el of this.frame.querySelectorAll('[data-screen]')) {
      el.hidden = el.dataset.screen !== screen || (screen === 'step' && Number(el.dataset.index) !== step);
    }
    const progress = screen === 'step' ? (step + 1) / this.steps.length : 1;
    this.frame.style.setProperty('--quiz-progress', `${progress * 100}%`);
    this.frame.scrollTop = 0;

    if (screen === 'step') this.renderStep();
    if (screen === 'processing') this.process();
    if (screen === 'result') this.result();

    this.current()?.querySelector('[data-focus]')?.focus({ preventScroll: true });
  }

  renderStep() {
    const { step: index, answers } = this.state;
    const step = this.steps[index];
    if (!step) return this.go('intro');

    const options = [...step.el.querySelectorAll('[data-option]')];
    const set = this.items ? this.lib.narrow(this.items, this.steps, answers, index) : null;
    step.set = set;

    for (const option of options) {
      const count = set && step.mode !== 'offer'
        ? set.filter((product) => this.lib.has(product, step.key, option.dataset.value)).length
        : null;
      option.dataset.count = count ?? '';
      option.parentElement.hidden = count === 0;
      option.setAttribute('aria-pressed', String(answers[step.key] === option.dataset.value));
    }

    step.visible = options.filter((option) => !option.parentElement.hidden);

    // No product in play carries this metafield: skip the question.
    if (!step.visible.length) {
      delete answers[step.key];
      const next = index + (this.direction || 1);
      if (next < 0) return this.go('intro');
      if (next >= this.steps.length) return this.go('processing');
      return this.go('step', next, this.direction);
    }

    this.pill(step);

    const hint = step.el.querySelector('[data-hint]');
    const keys = [...KEYS.slice(0, step.visible.length)];
    const last = keys.pop();
    const list = keys.length ? `${keys.join(', ')} ${hint.dataset.or} ${last}` : last;
    hint.textContent = (step.visible.length === 1 ? hint.dataset.one : hint.dataset.other).replace('[keys]', list);
  }

  pill(step, count = step.set?.length) {
    const pill = step.el.querySelector('[data-pill]');
    if (!pill) return;
    pill.hidden = count == null;
    const text = pill.querySelector('[data-pill-count]');
    if (count != null) text.textContent = plural(text, count);
  }

  // Hover or focus previews how many pieces an option leaves.
  onHover = (event) => {
    if (this.state.screen !== 'step' || this.busy) return;
    const step = this.steps[this.state.step];
    if (step?.mode !== 'filter' || !step.set) return;
    const option = event.target.closest?.('[data-option]');
    const count = option && option.dataset.count !== '' ? Number(option.dataset.count) : step.set.length;
    this.pill(step, count);
  };

  onKey = (event) => {
    if (this.state.screen !== 'step' || event.key.length !== 1) return;
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const option = this.steps[this.state.step]?.visible?.[KEYS.indexOf(event.key.toUpperCase())];
    if (!option) return;
    event.preventDefault();
    option.focus();
    this.choose(option);
  };

  choose(option) {
    const step = this.steps[this.state.step];
    if (this.busy || this.state.screen !== 'step' || !step?.el.contains(option)) return;
    this.busy = true;

    for (const el of step.el.querySelectorAll('[data-option]')) el.setAttribute('aria-pressed', String(el === option));
    if (step.mode === 'filter' && option.dataset.count !== '') this.pill(step, Number(option.dataset.count));

    this.state.answers[step.key] = option.dataset.value;
    this.save();

    this.timer = setTimeout(() => {
      const next = this.state.step + 1;
      if (next < this.steps.length) this.go('step', next);
      else this.go('processing');
    }, reduced() ? 0 : PICK);
  }

  back() {
    const { screen, step } = this.state;
    if (screen !== 'step' || step === 0) return this.go('intro');
    this.go('step', step - 1, -1);
  }

  // The processing log and the result live in assets/quiz-result.js.
  process() {
    this.load().then(() => this.view?.process(this));
  }

  result() {
    this.load().then(() => this.view?.result(this));
  }
}

if (!customElements.get('quiz-flow')) {
  customElements.define('quiz-flow', QuizFlow);
}
